import {StyleSheet} from "aphrodite";
import {useId, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {doNotTranslateYet} from "@repo/i18n";
import {mediaQueries} from "@repo/shared-styles";

type Props = {
    kind: React.ComponentProps<typeof Button>["kind"];
    style?: StyleType;
    currentTitle: string;
    onSave: (newTitle: string) => unknown;
};

export default function EditTitleButton({
    kind,
    style,
    currentTitle,
    onSave,
}: Props): React.ReactElement {
    return (
        <ModalLauncher
            modal={({closeModal}) => (
                <EditTitleModal
                    currentTitle={currentTitle}
                    onSave={onSave}
                    onClose={closeModal}
                />
            )}
        >
            {({openModal}) => (
                <Button kind={kind} onClick={openModal} style={style}>
                    {doNotTranslateYet`Edit title`}
                </Button>
            )}
        </ModalLauncher>
    );
}

function EditTitleModal({
    currentTitle: initialTitle,
    onSave,
    onClose,
}: {
    currentTitle: string;
    onSave: (newTitle: string) => unknown;
    onClose: () => unknown;
}): React.ReactElement {
    const [newTitle, setNewTitle] = useState(initialTitle);
    const inputId = useId();

    const isNewTitleInvalid = newTitle.trim() === "";

    return (
        <OnePaneDialog
            style={styles.dialog}
            title={doNotTranslateYet`Edit title`}
            content={
                <>
                    <label className="sr-only" htmlFor={inputId}>
                        {doNotTranslateYet`New title`}
                    </label>
                    <TextField
                        value={newTitle}
                        onChange={setNewTitle}
                        id={inputId}
                    />
                </>
            }
            footer={
                <View style={styles.footer}>
                    <Button kind="tertiary" onClick={onClose}>
                        {doNotTranslateYet`Cancel`}
                    </Button>
                    <Button
                        onClick={() => {
                            onSave(newTitle);
                            onClose();
                        }}
                        disabled={isNewTitleInvalid}
                    >
                        {doNotTranslateYet`Save`}
                    </Button>
                </View>
            }
        />
    );
}

const styles = StyleSheet.create({
    dialog: {
        maxHeight: 244,
        [mediaQueries.smOrSmaller]: {
            maxHeight: 276,
        },
    },
    footer: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-end",
        gap: spacing.medium_16,
    },
});
